import { memo, useCallback, useMemo } from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  SurveyQuestion,
  QuestionKind,
  DISTANCE_CHOICES,
  YES_NO_CHOICES,
} from '../../constants/loungeQuestions';
import SurveyStarRating from './SurveyStarRating';
import SurveyChoiceButtons from './SurveyChoiceButtons';

interface SurveyQuestionCardProps {
  question: SurveyQuestion;
  value: number | null;
  onChange: (questionId: number, value: number | null) => void;
  masseuseName?: string;
  disabled?: boolean;
}

// Static sx objects moved outside component to prevent recreation
const cardSx = {
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  border: 'none',
  borderRadius: { xs: 2, sm: 3 },
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  mb: { xs: 2, sm: 3 },
  // Removed transition and hover effects that cause repaints during scroll
} as const;

const cardContentSx = {
  p: { xs: 2, sm: 4, md: 5 },
  '&:last-child': { pb: { xs: 2, sm: 4, md: 5 } },
} as const;

const questionBoxSx = { mb: { xs: 2, sm: 3.5, md: 4 } } as const;

const questionTypographySx = {
  fontSize: {
    xs: '1.1rem',
    sm: '1.5rem',
    md: '1.6rem',
    lg: '1.4rem',
  },
  fontWeight: 600,
  lineHeight: 1.5,
  color: 'text.primary',
} as const;

const masseuseNameSx = {
  color: 'primary.main',
  fontWeight: 700,
} as const;

const helperTextSx = {
  fontSize: {
    xs: '0.8rem',
    sm: '0.9rem',
    md: '0.95rem',
  },
  color: 'text.secondary',
  fontWeight: 400,
  lineHeight: 1.5,
  mt: 1,
  fontStyle: 'italic',
} as const;

const SurveyQuestionCard = memo(function SurveyQuestionCard({
  question,
  value,
  onChange,
  masseuseName,
  disabled = false,
}: SurveyQuestionCardProps) {
  // Memoize the change handler to prevent child re-renders
  const handleChange = useCallback(
    (newValue: number | null) => {
      onChange(question.id, newValue);
    },
    [onChange, question.id],
  );

  // Memoize the question text to prevent recalculation
  const questionText = useMemo(
    () => (masseuseName ? `${question.statement} ` : question.statement),
    [masseuseName, question.statement],
  );

  // Memoize the question input to prevent unnecessary re-renders
  const questionInput = useMemo(() => {
    switch (question.kind) {
      case QuestionKind.RATING:
        return (
          <SurveyStarRating
            value={value}
            onChange={handleChange}
            disabled={disabled}
          />
        );

      case QuestionKind.YES_NO:
        return (
          <SurveyChoiceButtons
            options={YES_NO_CHOICES}
            value={value}
            onChange={handleChange}
            disabled={disabled}
          />
        );

      case QuestionKind.THREE_CHOICES:
        return (
          <SurveyChoiceButtons
            options={DISTANCE_CHOICES}
            value={value}
            onChange={handleChange}
            disabled={disabled}
          />
        );

      case QuestionKind.FIVE_CHOICES:
        return <Typography color="text.secondary">Not implemented</Typography>;

      case QuestionKind.TEXT:
        return <Typography color="text.secondary">Not implemented</Typography>;

      default:
        return null;
    }
  }, [question.kind, value, handleChange, disabled]);

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent sx={cardContentSx}>
        <Box sx={questionBoxSx}>
          <Typography variant="h6" component="h2" sx={questionTypographySx}>
            {questionText}
            {masseuseName && (
              <Box component="span" sx={masseuseNameSx}>
                {masseuseName.toUpperCase()}
              </Box>
            )}
          </Typography>
          {question.helperText && (
            <Typography sx={helperTextSx}>{question.helperText}</Typography>
          )}
        </Box>

        {questionInput}
      </CardContent>
    </Card>
  );
});

export default SurveyQuestionCard;
